import React, { useEffect, useRef } from 'react';
import LockResetIcon from '@mui/icons-material/LockReset';
import { QuickbaseHostBridge } from '../quickbase-host-bridge/quickbase-host-bridge';
import { QuickbaseAuthFlow } from '../quickbase-types';
import { QB_IFRAME_SANDBOX, VerifiedQuickbaseEmbed } from './VerifiedQuickbaseEmbed';
import {
	AuthOverlay,
	ErrorContainer,
	ErrorMessage,
	FloatingAuthButton,
	IframeContainer,
	RetryButton,
	StyledIframe,
} from './quickbase-iframe.styles';

export type QuickbaseIFrameType =
	| 'Utilities'
	| 'NxTension'
	| 'Dashboard'
	| 'NxOrders'
	| 'NxInventory'
	| 'Admin'
	| 'Orders'
	| 'Inventory';

export interface QuickbaseIFrameContainerProps {
	isLoading: boolean;
	isAuthenticated: boolean;
	error: string | null;
	embedUrl: string | null;
	frameUrl?: string | null;
	showAuthOverlay: boolean;
	authenticate(): Promise<void>;
	setShowAuthOverlay(show: boolean): void;
	dismissAuthOverlay(): void;
	recoverSession?(): Promise<boolean>;
	iFrameType?: QuickbaseIFrameType;
	authFlow?: QuickbaseAuthFlow;
	probeTimeoutMs?: number;
	probeGraceMs?: number;
	bridge?: QuickbaseHostBridge | null;
	reloadToken?: number;
}

export function QuickbaseIFrameContainer(props: QuickbaseIFrameContainerProps) {
	const {
		error,
		embedUrl,
		authenticate,
		authFlow = 'legacy',
		iFrameType = 'NxTension',
		frameUrl,
		recoverSession,
		probeTimeoutMs,
		probeGraceMs,
		bridge,
		reloadToken,
	} = props;

	// Show QuickBase authentication error
	if (error) {
		return (
			<ErrorContainer>
				<ErrorMessage>Authentication Error</ErrorMessage>
				<div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>{error}</div>
				<RetryButton onClick={() => authenticate()}>Retry Authentication</RetryButton>
			</ErrorContainer>
		);
	}

	// Show configuration error if no embed URL
	if (!embedUrl) {
		return (
			<ErrorContainer>
				<ErrorMessage>Integration is not configured</ErrorMessage>
			</ErrorContainer>
		);
	}

	if (authFlow === 'verified') {
		return (
			<VerifiedQuickbaseEmbed
				embedUrl={embedUrl}
				frameUrl={frameUrl}
				iFrameType={iFrameType}
				recoverSession={recoverSession}
				probeTimeoutMs={probeTimeoutMs}
				probeGraceMs={probeGraceMs}
				bridge={bridge}
				reloadToken={reloadToken}
			/>
		);
	}

	return <LegacyQuickbaseEmbed {...props} embedUrl={embedUrl} iFrameType={iFrameType} />;
}

type LegacyQuickbaseEmbedProps = Omit<QuickbaseIFrameContainerProps, 'embedUrl' | 'iFrameType'> & {
	embedUrl: string;
	iFrameType: QuickbaseIFrameType;
};

function LegacyQuickbaseEmbed({
	isLoading,
	isAuthenticated,
	embedUrl,
	showAuthOverlay,
	authenticate,
	setShowAuthOverlay,
	dismissAuthOverlay,
	iFrameType,
	bridge = null,
	reloadToken = 0,
}: LegacyQuickbaseEmbedProps) {
	const iframeRef = useRef<HTMLIFrameElement>(null);

	// Refresh iframe when authentication status changes
	useEffect(() => {
		if (iframeRef.current && embedUrl) {
			iframeRef.current.src = embedUrl;
		}
	}, [isAuthenticated, embedUrl, reloadToken]);

	useEffect(() => {
		if (!bridge || !iframeRef.current) return undefined;
		return bridge.attach(iframeRef.current);
	}, [bridge]);

	// Handle authentication
	const handleQuickBaseAuthentication = async () => {
		await authenticate();
	};

	// Handle iframe load/error events
	const handleIframeLoad = () => {
		// Show auth overlay after a delay if needed
		if (!isAuthenticated && !isLoading) {
			setTimeout(() => {
				if (!isAuthenticated && !isLoading) {
					setShowAuthOverlay(true);
				}
			}, 3000);
		}
	};

	const handleIframeError = () => {
		if (!isAuthenticated && !isLoading) {
			setShowAuthOverlay(true);
		}
	};

	return (
		<IframeContainer>
			<StyledIframe
				ref={iframeRef}
				src={embedUrl}
				sandbox={QB_IFRAME_SANDBOX}
				onLoad={handleIframeLoad}
				onError={handleIframeError}
				title={`${iFrameType} Quickbase IFrame`}
			/>

			{/* Floating auth button */}
			{!isLoading && !showAuthOverlay && isAuthenticated && (
				<FloatingAuthButton
					onClick={handleQuickBaseAuthentication}
					title="Refresh Authentication"
					aria-label="Refresh Authentication"
				>
					<div className="icons">
						<LockResetIcon />
					</div>
					<span className="label">Refresh Auth</span>
				</FloatingAuthButton>
			)}

			{/* Show authentication overlay when needed */}
			{(showAuthOverlay || isLoading) && !isAuthenticated && (
				<AuthOverlay>
					<ErrorContainer>
						{isLoading ? (
							<>
								<ErrorMessage>Authenticating with Quickbase...</ErrorMessage>
								<div style={{ fontSize: '14px', color: '#666' }}>
									Please complete authentication in the popup window and close the popup when done.
								</div>
							</>
						) : (
							<>
								<ErrorMessage>Quickbase Authentication</ErrorMessage>
								<div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
									If you see a login screen or need to authenticate, click the button below. If you are already logged
									in, you can dismiss this overlay.
								</div>
								<div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
									<RetryButton onClick={handleQuickBaseAuthentication}>Authenticate with {iFrameType}</RetryButton>
									<RetryButton onClick={dismissAuthOverlay} style={{ background: '#666' }}>
										I am Already Logged In
									</RetryButton>
								</div>
							</>
						)}
					</ErrorContainer>
				</AuthOverlay>
			)}
		</IframeContainer>
	);
}
